import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  getEventSessions,
  createEventSession,
  updateEventSession,
  deleteEventSession
} from '../../services/eventsService';
import { handleOrientationSessionUpdate } from '../../services/orientationRegistrationsService';
import {
  getSessionInstructors,
  addInstructorToSession,
  removeInstructorFromSession,
  getAllInstructors
} from '../../services/instructorsService';
import {
  DEFAULT_EVENT_TIMEZONE,
  isoToSessionLocal,
  sessionLocalToIso,
  formatSessionDateTimeWithZone,
  shortTimezoneLabel,
  sortEventSessionsByStartTime
} from '../../utils/sessionTime';
import { getEventLocationFieldLabels } from '../../utils/eventLocation';

const { FiPlus, FiEdit2, FiTrash2, FiCalendar, FiClock, FiX, FiUsers } = FiIcons;

export default function SessionsTab({
  eventId,
  sessions: initialSessions,
  orientationSessionId,
  onUpdate,
  eventTimezone = DEFAULT_EVENT_TIMEZONE,
  eventFormat = 'online'
}) {
  const [sessions, setSessions] = useState(initialSessions || []);
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialSessions) {
      setSessions(sortEventSessionsByStartTime(initialSessions));
    } else {
      loadSessions();
    }
  }, [eventId, initialSessions]);

  async function loadSessions() {
    try {
      setLoading(true);
      const { data, error } = await getEventSessions(eventId);
      if (error) throw error;
      setSessions(sortEventSessionsByStartTime(data || []));
    } catch (error) {
      console.error('[SessionsTab] Error loading sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(session) {
    setEditingSession(session);
    setShowModal(true);
  }

  function handleAdd() {
    setEditingSession(null);
    setShowModal(true);
  }

  async function handleDelete(sessionId) {
    if (!window.confirm('Are you sure you want to delete this session?')) return;

    try {
      const { error } = await deleteEventSession(sessionId);
      if (error) throw error;

      await loadSessions();
      toast.success('Session deleted');
      onUpdate?.();
    } catch (error) {
      console.error('[SessionsTab] Error deleting session:', error);
      toast.error('Failed to delete session');
    }
  }

  async function handleSaveSession(sessionData) {
    try {
      if (editingSession) {
        const { error } = await updateEventSession(editingSession.id, sessionData);
        if (error) throw error;
        if (orientationSessionId && editingSession.id === orientationSessionId) {
          const { error: updateErr } = await handleOrientationSessionUpdate(editingSession.id);
          if (updateErr) {
            toast.error('Session saved, but email reschedule failed: ' + (updateErr?.message || 'Unknown error'));
          }
        }
        toast.success('Session updated');
      } else {
        const { error } = await createEventSession({ ...sessionData, event_id: eventId });
        if (error) throw error;
        toast.success('Session created');
      }

      setShowModal(false);
      setEditingSession(null);
      await loadSessions();
      onUpdate?.();
    } catch (error) {
      console.error('[SessionsTab] Error saving session:', error);
      toast.error('Failed to save session');
    }
  }

  function formatDateTime(dateString) {
    if (!dateString) return 'Not set';
    return formatSessionDateTimeWithZone(dateString, eventTimezone);
  }

  if (loading && !sessions.length) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Event Sessions</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage individual sessions for this event
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Add Session
        </button>
      </div>

      {/* Sessions List */}
      <div className="space-y-4">
        {sessions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <SafeIcon icon={FiCalendar} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No sessions created yet</p>
            <button
              onClick={handleAdd}
              className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first session
            </button>
          </div>
        ) : (
          sessions.map((session, index) => (
            <div key={session.id} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                      Session {index + 1}
                    </span>
                    {session.session_type && (
                      <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {session.session_type}
                      </span>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {session.title || `Session ${index + 1}`}
                  </h3>

                  {session.description && (
                    <p className="text-gray-600 mb-3 text-sm">{session.description}</p>
                  )}

                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1" />
                      {formatDateTime(session.start_time)}
                    </div>
                    {session.duration_minutes && (
                      <div className="flex items-center">
                        <SafeIcon icon={FiClock} className="w-4 h-4 mr-1" />
                        {session.duration_minutes} minutes
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(session)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit session"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(session.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete session"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Session Modal */}
      {showModal && (
        <SessionModal
          eventId={eventId}
          session={editingSession}
          eventTimezone={eventTimezone}
          eventFormat={eventFormat}
          onSave={handleSaveSession}
          onClose={() => {
            setShowModal(false);
            setEditingSession(null);
          }}
        />
      )}
    </div>
  );
}

// Session Edit/Create Modal
function SessionModal({ eventId, session, eventTimezone, eventFormat, onSave, onClose }) {
  const locationLabels = getEventLocationFieldLabels(eventFormat);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    session_type: 'lecture',
    start_time: '',
    end_time: '',
    duration_minutes: 60,
    location_name: '',
    location_address: '',
    meeting_url: '',
    materials_url: ''
  });
  const [saving, setSaving] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [sessionInstructors, setSessionInstructors] = useState([]);
  const [showInstructorDropdown, setShowInstructorDropdown] = useState(false);

  useEffect(() => {
    setFormData({
      title: session?.title || '',
      description: session?.description || '',
      session_type: session?.session_type || 'lecture',
      start_time: session?.start_time ? isoToSessionLocal(session.start_time, eventTimezone) : '',
      end_time: session?.end_time ? isoToSessionLocal(session.end_time, eventTimezone) : '',
      duration_minutes: session?.duration_minutes || 60,
      location_name: session?.location_name ?? session?.location ?? '',
      location_address: session?.location_address ?? '',
      meeting_url: session?.meeting_url || '',
      materials_url: session?.materials_url || ''
    });
  }, [session, eventTimezone]);

  useEffect(() => {
    loadInstructors();
  }, [session]);

  async function loadInstructors() {
    try {
      const { data: allInst } = await getAllInstructors();
      setInstructors(allInst || []);

      if (session?.id) {
        const { data: sessInst } = await getSessionInstructors(session.id);
        setSessionInstructors(sessInst || []);
      }
    } catch (error) {
      console.error('[SessionModal] Error loading instructors:', error);
    }
  }

  async function handleAddSessionInstructor(instructorId) {
    if (!session?.id) {
      toast.error('Save the session first before adding instructors');
      return;
    }

    try {
      const { error } = await addInstructorToSession(session.id, instructorId);
      if (error) throw error;

      await loadInstructors();
      setShowInstructorDropdown(false);
      toast.success('Instructor added to session');
    } catch (error) {
      console.error('[SessionModal] Error adding instructor:', error);
      toast.error('Failed to add instructor');
    }
  }

  async function handleRemoveSessionInstructor(instructorId) {
    try {
      const { error } = await removeInstructorFromSession(session.id, instructorId);
      if (error) throw error;

      await loadInstructors();
      toast.success('Instructor removed from session');
    } catch (error) {
      console.error('[SessionModal] Error removing instructor:', error);
      toast.error('Failed to remove instructor');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Session title is required');
      return;
    }

    setSaving(true);
    await onSave({
      ...formData,
      start_time: sessionLocalToIso(formData.start_time, eventTimezone),
      end_time: sessionLocalToIso(formData.end_time, eventTimezone)
    });
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {session ? 'Edit Session' : 'Add Session'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 max-h-[60vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Session Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session Type
              </label>
              <select
                value={formData.session_type}
                onChange={(e) => setFormData({ ...formData, session_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="lecture">Lecture</option>
                <option value="workshop">Workshop</option>
                <option value="q_and_a">Q&A</option>
                <option value="break">Break</option>
                <option value="networking">Networking</option>
                <option value="panel">Panel Discussion</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (minutes)
              </label>
              <input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="datetime-local"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="datetime-local"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2">
            Times are in {shortTimezoneLabel(eventTimezone)} ({eventTimezone}). Change the event time zone on the Basic Info tab if needed.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {locationLabels.nameLabel}
            </label>
            <input
              type="text"
              value={formData.location_name}
              onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={locationLabels.namePlaceholder}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {locationLabels.addressLabel}
            </label>
            <input
              type="text"
              value={formData.location_address}
              onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={locationLabels.addressPlaceholder}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meeting URL
            </label>
            <input
              type="url"
              value={formData.meeting_url}
              onChange={(e) => setFormData({ ...formData, meeting_url: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="https://zoom.us/..."
            />
          </div>

          {/* Session Instructors (only for existing sessions) */}
          {session?.id && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Instructors
              </label>
              <div className="space-y-2">
                {sessionInstructors.map((si) => (
                  <div key={si.instructor_id} className="flex items-center justify-between bg-gray-50 rounded p-2">
                    <span className="text-sm">{si.instructor?.full_name || 'Unknown'}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSessionInstructor(si.instructor_id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4" />
                    </button>
                  </div>
                ))}

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowInstructorDropdown(!showInstructorDropdown)}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                    Add Instructor
                  </button>

                  {showInstructorDropdown && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {instructors
                        .filter(i => !sessionInstructors.some(si => si.instructor_id === i.id))
                        .map((inst) => (
                          <button
                            key={inst.id}
                            type="button"
                            onClick={() => handleAddSessionInstructor(inst.id)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                          >
                            {inst.full_name}
                          </button>
                        ))}
                      {instructors.filter(i => !sessionInstructors.some(si => si.instructor_id === i.id)).length === 0 && (
                        <p className="px-3 py-2 text-sm text-gray-500">No instructors available</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : session ? 'Update Session' : 'Create Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
